/**
 * @file Functions used in sorting project outlines.
 */

import find from "lodash/find.js";
import get from "lodash/get.js";
import moment from "moment";
import Account from "../account.js";
import Project from "../project.js";
import {COLOR_TYPES, TOPIC_COLORS} from "../constants/colors.js";

const availableTypes = ["source", "date", "editor"];

/**
 * Function to be used with createSelector() from reselect
 *
 * @param {object} myAccount
 * @param {object} project
 * @param {array}  outline      Raw outline
 * @param {array}  topics
 * @param {array}  annotations
 * @param {string} sortBy       topic or one of availableTypes
 */
export function getVirtualOutline(myAccount: any, project: any, outline: any, topics: any, annotations: any, sortBy: any) {
	// Renderers for topic/group headings
	const renders = {
		// @ts-expect-error TS(7006) FIXME: Parameter 'date' implicitly has an 'any' type.
		date:  (date) => moment(date, "YYYY-MM-DD").format("MMMM D, YYYY"),
		// @ts-expect-error TS(7006) FIXME: Parameter 'key' implicitly has an 'any' type.
		topic: (key) => {
			let renderKey = key;
			const topic = find(topics, {uuid: key});

			if (topic) renderKey = topic.label;
			else if (renderKey === "bookmarks") renderKey = "Bookmarks";

			return renderKey;
		},
		// @ts-expect-error TS(7006) FIXME: Parameter 'accountUuid' implicitly has an 'any' ty... Remove this comment to see the full error message
		editor: (accountUuid) => {
			if (accountUuid === "__PROJECT_OWNER__") {
				// This is a special case where we use a weird placeholder to prevent collisions in sort keys.
				accountUuid = Project.getOwnerUuid(project);
			}

			const myAccountUuid = Account.getUuidFromAccount(myAccount);
			// @ts-expect-error FIXME
			return Project.getUserNameFromShare(accountUuid, myAccountUuid, project);
		}
	};

	const topicsCache = {};
	// @ts-expect-error TS(7006) FIXME: Parameter 'topicUuid' implicitly has an 'any' type... Remove this comment to see the full error message
	const getTopic = (topicUuid, topicType) => {
		const key = topicUuid || topicType;

		// @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
		if (!topicsCache[key]) {
			if (typeof topicUuid !== "undefined")
			// @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
				topicsCache[key] = find(topics, {uuid: topicUuid}) || null;
			else if (topicType) {
				switch (topicType) {
					case "bookmarks":
						// @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
						topicsCache[key] = {color: TOPIC_COLORS[COLOR_TYPES.DEFAULT].bookmarks, label: "Bookmarks", type: "bookmarks"};
						break;
				}
			}
		}

		// @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
		return topicsCache[key];
	};

	// @ts-expect-error TS(7006) FIXME: Parameter 'uuid' implicitly has an 'any' type.
	const sortedOutline = sortOutline(sortBy, outline, uuid => annotations[uuid], renders[sortBy]);

	// @ts-expect-error TS(7006) FIXME: Parameter 'outlineTopic' implicitly has an 'any' t... Remove this comment to see the full error message
	const virtualOutline = sortedOutline.map(outlineTopic => {
		const topic = getTopic(outlineTopic.topic_uuid, outlineTopic.type);

		return {
			...outlineTopic,
			topic,
			annotations: outlineTopic.annotations
			// @ts-expect-error TS(7006): Parameter 'a' implicitly has an 'any' type.
				.filter(a => !!a.annotation_uuid) // Skip entries with no `annotation_uuid` (bad outline data, it's happened before)
			// @ts-expect-error TS(7006): Parameter 'a' implicitly has an 'any' type.
				.map(a => {
					const annotationTopic = getTopic(a.topic.topic_uuid, a.topic.type);
					if (!annotationTopic)
						console.error("Encountered topic from another project: " + a.topic.topic_uuid);

					const annotationData = annotations[a.annotation_uuid];
					if (!annotationData)
						return {}; // Don't blow up the page if we can't find an annotation

					const currentAccountUuid = Account.getUuidFromAccount(myAccount);
					// If created_by does not exist, assume that it is the project owner
					const createdByUuid = annotationData.created_by ? annotationData.created_by : project.account_uuid;
					// @ts-expect-error FIXME
					const createdByName = Project.getUserNameFromShare(createdByUuid, currentAccountUuid, project);

					// Hydrate the annotation placeholder with the real data,
					// plus additional helpful things like the full topic data
					return {
						...annotationData,
						createdByName,
						topic: annotationTopic
					};
				})
			// @ts-expect-error TS(7006): Parameter 'ot' implicitly has an 'any' type.
				.filter(ot => !!ot.topic) // Exclude all annotations without topic
		};
	});

	return virtualOutline;
};

/**
 * Creates a virtual outline grouped by specified type
 *
 * @param {string}   type            topic or one of availableTypes
 * @param {array}    outline         Raw outline
 * @param {function} getAnnotation   Function to get annotation information
 * @param {function} render          Function to render the section label
 */
const sortOutline = function (
	type = "default",
	// @ts-expect-error TS(7006) FIXME: Parameter 'outline' implicitly has an 'any' type.
	outline,
	getAnnotation = () => undefined,
	// @ts-expect-error TS(7006) FIXME: Parameter 'render' implicitly has an 'any' type.
	render
) {
	if (availableTypes.includes(type))
		return customOutlineSort(type, outline, getAnnotation, render);
	else
		return defaultOutlineSort(type, outline, getAnnotation, render);
};

/**************************************************
 *               Sorting definitions              *
 **************************************************/

// Functions for getting annotation/card keys by sort type
const keyByTypeMap = {
// @ts-expect-error TS(7006) FIXME: Parameter 'annotation' implicitly has an 'any' typ... Remove this comment to see the full error message
	source: annotation => get(annotation, "pageData.pageTitle", null),
	// @ts-expect-error TS(7006) FIXME: Parameter 'annotation' implicitly has an 'any' typ... Remove this comment to see the full error message
	date:   annotation => {
		let createdDt = get(annotation, "created_dt", null);

		if (createdDt) {
			createdDt = new Date(createdDt);
			return `${createdDt.getFullYear()}-${createdDt.getMonth() + 1}-${createdDt.getDate()}`;
		}

		return undefined;
	},
	// @ts-expect-error TS(7006) FIXME: Parameter 'annotation' implicitly has an 'any' typ... Remove this comment to see the full error message
	editor: annotation => {
		// special case '__PROJECT_OWNER__' is resolved for display in the "render function".
		// See getVirtualOutlineSelector() in components/project/outline/index/page.jsx
		return get(annotation, "created_by", "__PROJECT_OWNER__");
	}
};

// Helpful sorting function for strings and other things...
// @ts-expect-error TS(7006) FIXME: Parameter 'a' implicitly has an 'any' type.
const defaultSort = (a, b) => {
	if (a === b)
		return 0;
	else
		return a < b ? -1 : 1;
};

// Functions for sorting groups of items in the outline (topics, dates, etc.)
const groupSortFnByType = {
// @ts-expect-error TS(7006) FIXME: Parameter 'a' implicitly has an 'any' type.
	date: (a, b) => {
		// @ts-expect-error TS(2362) FIXME: The left-hand side of an arithmetic operation must... Remove this comment to see the full error message
		return new Date(a.label) - new Date(b.label);
	},

	// @ts-expect-error TS(7006) FIXME: Parameter 'a' implicitly has an 'any' type.
	editor: (a, b) => {
		// Ideally we would always put "Me" at the top,
		// but without relying on a direct string match
		// we don't have an easy way to do that from here.
		return defaultSort(a.render, b.render);
	},

	// @ts-expect-error TS(7006) FIXME: Parameter 'a' implicitly has an 'any' type.
	default: (a, b) => defaultSort(a.label, b.label)
};

// Functions for sorting contents of group items (so, individual annotations/cards within a group)
// @ts-expect-error TS(7006) FIXME: Parameter 'cacheAnnotations' implicitly has an 'an... Remove this comment to see the full error message
const contentsSortFnByType = (cacheAnnotations) => {
// @ts-expect-error TS(7006) FIXME: Parameter 'a' implicitly has an 'any' type.
	const dateSort = (a, b) => {
		// Within date groupings, sort cards by created_dt

		const A = cacheAnnotations[a.annotation_uuid];
		const B = cacheAnnotations[b.annotation_uuid];

		const keyA = get(A, "created_dt", null);
		const keyB = get(B, "created_dt", null);

		return defaultSort(keyA, keyB);
	};

	return {
		date:   dateSort,
		editor: dateSort
	};
};

/**
 * Creates a virtual outline grouped by source, date, and topic,
 * using the sorting selected by the user.
 *
 * @param {string}   type            date or source
 * @param {array}    outline         Raw outline
 * @param {function} getAnnotation   Function to get annotation information
 * @param {function} render          Function to render the section label
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'type' implicitly has an 'any' type.
function customOutlineSort(type, outline, getAnnotation, render) {
// @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
	const getKey = keyByTypeMap[type] || (() => null);
	// @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
	const sortFn = groupSortFnByType[type] || groupSortFnByType["default"];

	const cacheAnnotations = {};

	// @ts-expect-error TS(7006) FIXME: Parameter 'acc' implicitly has an 'any' type.
	const output = Object.values(outline.reduce((acc, entry) => {
		const {topic_uuid, type, annotations} = entry;

		// @ts-expect-error TS(7006) FIXME: Parameter 'annotation' implicitly has an 'any' typ... Remove this comment to see the full error message
		annotations.forEach(annotation => {
			const {annotation_uuid} = annotation;

			// @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
			if (!cacheAnnotations[annotation_uuid])
			// @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
				cacheAnnotations[annotation_uuid] = getAnnotation(annotation_uuid);

			// @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
			const annotationDetails = cacheAnnotations[annotation_uuid];
			const key = getKey(annotationDetails, entry);

			if (key) {
				acc[key] = acc[key] || {
					label:       key,
					render:      typeof render === "function" ? render(key, entry) : key,
					annotations: []
				};

				acc[key].annotations.push({
					...annotation,
					topic: {topic_uuid, type}
				});
			}
		});

		return acc;
	}, {}));

	// Sort top-level groupings in output
	output.sort(sortFn);

	// Sort individual cards within top-level groupings
	output.forEach((topic) => {
		// @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
		const sortFn = contentsSortFnByType(cacheAnnotations)[type] || null;
		if (sortFn)
		// @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
			topic.annotations.sort(sortFn);
	});

	return output;
};

/**
 * Creates a virtual outline grouped by source, date, and topic,
 * using the default outline layout defined by the user.
 *
 * @param {string}   type            who knows? hopefully "default"
 * @param {array}    outline         Raw outline
 * @param {function} getAnnotation   Function to get annotation information
 * @param {function} render          Function to render the section label
 */
// @ts-expect-error TS(7006) FIXME: Parameter 'type' implicitly has an 'any' type.
function defaultOutlineSort(type, outline, getAnnotation, render) {
// @ts-expect-error TS(7006) FIXME: Parameter 'entry' implicitly has an 'any' type.
	return outline.map(entry => {
		const {topic_uuid, type, annotations} = entry;
		const key = topic_uuid || type;

		return {
			label:       key,
			render:      typeof render === "function" ? render(key, entry) : key,
			topic_uuid,
			type,
			// @ts-expect-error TS(7006) FIXME: Parameter 'annotation' implicitly has an 'any' typ... Remove this comment to see the full error message
			annotations: annotations.map(annotation => ({
				...annotation,
				topic: {topic_uuid, type}
			}))
		};
	});
};

export default {
	getVirtualOutline
};
